/// <amd-module name="Feed/Core/Redux/FeedHub/FeedHubSelectors" />
import { FeedMessage, UpstreamHealthStatus } from "Feed/Common/Contracts/WebPage";
import { IError } from "Feed/Common/Types/IError";
import { PackagingService, TfsService } from "Feed/Core/PackagingService";
import { IUxFeedView, UxFeedView } from "Feed/Core/UxFeedView";
import * as Reselect from "Reselect";

import { FeedDialogData, IFeedData, IFeedHubDataState, IFeedHubViewState } from "./FeedHubContracts";

export function getFeedHubDataState(state: IFeedHubViewState): IFeedHubDataState | undefined {
    return state && state.data;
}

export function getSelectedFeedId(state: IFeedHubViewState): string | undefined {
    return state.data.selectedFeedId;
}

export function getSelectedViewId(state: IFeedHubViewState): string | undefined {
    return state.data.selectedViewId;
}

function getAllFeedData(state: IFeedHubViewState): { [key: string]: IFeedData } {
    return state.data.feeds;
}

const getSelectedFeedData = Reselect.createSelector(
    getSelectedFeedId,
    getAllFeedData,
    (selectedFeedId: string | undefined, feeds: { [key: string]: IFeedData }) => {
        if (feeds && selectedFeedId != null) {
            const feeddata = feeds[selectedFeedId];
            return feeddata;
        }
        return undefined;
    }
);

export const getSelectedUxFeedView = Reselect.createSelector<
    IFeedHubViewState,
    string | undefined,
    IFeedData | undefined,
    IUxFeedView | undefined
>(getSelectedViewId, getSelectedFeedData, (selectedViewId: string | undefined, feeddata: IFeedData) => {
    if (feeddata == undefined) {
        return undefined;
    }

    const feed = feeddata.feed;
    if (selectedViewId == null) {
        return new UxFeedView(feed);
    } else {
        const selectedView = feeddata.views![selectedViewId];
        if (selectedView == undefined) {
            return undefined;
        }
        return new UxFeedView(feed, selectedView);
    }
});

/**
 * Call to get the selected feed with no view data, even if a view is selected.
 * In general, getSelectedUxFeedView should be used instead of this.
 */
export const getSelectedUxParentFeed = Reselect.createSelector<
    IFeedHubViewState,
    IFeedData | undefined,
    IUxFeedView | undefined
>(getSelectedFeedData, (feeddata: IFeedData) => {
    if (feeddata == undefined) {
        return undefined;
    }

    const feed = feeddata.feed;
    return new UxFeedView(feed);
});

export function getSelectedFeedMessages(state: IFeedHubViewState): FeedMessage[] | undefined {
    const selectedFeed = getSelectedFeedData(state);
    if (selectedFeed == undefined) {
        return;
    }
    return selectedFeed.feedMessages;
}

export function showGlobalSettingsPanel(state: IFeedHubViewState): boolean {
    return state.ui.showGlobalSettings;
}

export function getErrorMessage(state: IFeedHubViewState): IError | undefined {
    return state.ui.errorMessage;
}

export function getDialogData(state: IFeedHubViewState): FeedDialogData | undefined {
    return state.ui.dialogData;
}

export function isAnonymousOrPublic(state: IFeedHubViewState): boolean {
    return state.ui.isAnonymous || state.ui.isPublic;
}

export function isMember(state: IFeedHubViewState): boolean {
    return !isAnonymousOrPublic(state);
}

export function isSelectedFeedDeleted(state: IFeedHubViewState): boolean {
    const selectedFeed = getSelectedFeedData(state);
    if (selectedFeed && selectedFeed.feed.deletedDate != null) {
        return true;
    }
    return false;
}

export function getUpstreamHealthStatuses(state: IFeedHubViewState): UpstreamHealthStatus[] | undefined {
    const selectedFeed = getSelectedFeedData(state);
    return selectedFeed?.upstreamHealthStatuses;
}

export function getPackagingService(state: IFeedHubViewState): PackagingService {
    return new PackagingService(state.data.packagingUrl);
}

export function getTfsService(state: IFeedHubViewState): TfsService {
    return new TfsService(state.data.sessionTokenUri);
}
